(() => {
  const text = value => typeof value === 'string' ? value.trim() : '';
  const identifier = value => /^[1-9]\d*$/.test(String(value)) ? String(value) : null;
  const numericText = value => String(Number(value));
  const normalizeQuery = value => text(value).toLocaleLowerCase().replace(/\s+/g, ' ');

  function parseWheelSpec(label) {
    const parts = text(label).split('|').map(part => part.trim());
    if (parts.length < 3) return null;
    const size = parts[0].match(/^(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)$/i);
    const bolt = parts[1].match(/^(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)$/i);
    const offset = parts[2].match(/^([+-]?\d+)\s*(?:mm)?$/i);
    if (!size || !bolt || !offset) return null;
    const diameter = Number(size[1]), width = Number(size[2]), holes = Number(bolt[1]);
    const pcd = Number(bolt[2]), et = Number(offset[1]);
    if (!(diameter >= 10 && diameter <= 30 && width >= 5 && width <= 15 && holes >= 3 && holes <= 10 && pcd >= 80 && pcd <= 250 && et >= -100 && et <= 100)) return null;
    const annotation = parts.slice(3).join(' | ').replace(/^\S+\s*/, '').trim();
    return { diameter, width, bolt: `${holes}x${numericText(pcd)}`, offset: et === 0 ? 0 : et, annotation };
  }

  function safeProductUrl(value, origin, variantId) {
    if (typeof value !== 'string' || !value.trim() || /[\\\u0000-\u0020]/.test(value) || value.startsWith('//')) return null;
    try {
      const base = new URL(origin);
      const url = new URL(value, base.origin);
      if (!/^https?:$/.test(url.protocol) || url.origin !== base.origin || url.username || url.password || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
      url.search = '';
      url.hash = '';
      if (variantId !== undefined) {
        if (!identifier(variantId)) return null;
        url.searchParams.set('variant', String(variantId));
      }
      return url.pathname + url.search;
    } catch { return null; }
  }

  function safeImageUrl(value, origin) {
    if (typeof value !== 'string' || !value.trim() || /[\\\u0000-\u0020]/.test(value)) return null;
    try {
      const url = new URL(value.startsWith('//') ? `https:${value}` : value, origin);
      const ownHost = new URL(origin).hostname;
      const allowed = url.hostname === 'cdn.shopify.com' || ([ownHost, 'kanseiwheels.com', 'www.kanseiwheels.com'].includes(url.hostname) && url.pathname.startsWith('/cdn/shop/'));
      return url.protocol === 'https:' && !url.username && !url.password && allowed ? url.href : null;
    } catch { return null; }
  }

  function normalizeCatalog(payload, origin) {
    if (!payload || !Array.isArray(payload.products)) throw new TypeError('Invalid wheel catalog');
    const products = [], productIds = new Set(), variantIds = new Set();
    let skipped = 0;
    payload.products.forEach(raw => {
      const id = identifier(raw?.id), title = text(raw?.title);
      const url = safeProductUrl(raw?.url, origin);
      const variants = [];
      if (!id || !title || !url || !Array.isArray(raw?.variants) || productIds.has(id)) {
        skipped += Array.isArray(raw?.variants) ? raw.variants.length : 1;
        return;
      }
      productIds.add(id);
      raw.variants.forEach(item => {
        const variantId = identifier(item?.id), spec = parseWheelSpec(item?.label), sku = text(item?.sku);
        const link = safeProductUrl(item?.url || url, origin, variantId);
        if (!variantId || variantIds.has(variantId) || !spec || !sku || !link || link.split('?')[0] !== url || !Number.isInteger(item.price) || item.price < 0 || !text(item.priceText) || typeof item.available !== 'boolean') {
          skipped += 1;
          return;
        }
        variantIds.add(variantId);
        const dimensions = `${spec.diameter}×${spec.width} | ${spec.bolt.replace('x', '×')} | ${spec.offset > 0 ? '+' : ''}${spec.offset} mm`;
        variants.push({ id: variantId, sku, label: dimensions, price: item.price, priceText: text(item.priceText), available: item.available, url: link, image: safeImageUrl(item.image, origin) || safeImageUrl(raw.image, origin), spec });
      });
      if (variants.length) products.push({ id, title, url, model: text(raw.model), finish: text(raw.finish), image: safeImageUrl(raw.image, origin), variants });
    });
    return { products, skipped };
  }

  function matchingProducts(products, filters = {}) {
    const tokens = normalizeQuery(filters.query).split(' ').filter(Boolean);
    return products.map(product => {
      const prefix = normalizeQuery(`${product.title} ${product.model} ${product.finish}`);
      const variants = product.variants.filter(variant => {
        if (filters.stock && !variant.available) return false;
        if (!['diameter', 'width', 'bolt', 'offset'].every(key => filters[key] === undefined || filters[key] === '' || String(variant.spec[key]) === String(filters[key]))) return false;
        const searchable = `${prefix} ${normalizeQuery(variant.sku)}`;
        return tokens.every(token => searchable.includes(token));
      });
      return variants.length ? { ...product, variants } : null;
    }).filter(Boolean);
  }

  function filterOptions(products, key) {
    const values = [...new Set(products.flatMap(product => product.variants.map(variant => variant.spec[key])))];
    return values.sort((a, b) => key === 'bolt' ? String(a).localeCompare(String(b), 'en', { numeric: true }) : a - b);
  }

  function railPosition(rects, viewport, rtl = false) {
    const count = rects.length;
    if (!count) return { index: 0, count: 0, atStart: true, atEnd: true };
    const first = rects[0], last = rects[count - 1], tolerance = 2;
    const atStart = rtl ? first.right <= viewport.right + tolerance : first.left >= viewport.left - tolerance;
    const atEnd = rtl ? last.left >= viewport.left - tolerance : last.right <= viewport.right + tolerance;
    const edge = rtl ? 'right' : 'left';
    let index = rects.reduce((nearest, rect, i) => Math.abs(rect[edge] - viewport[edge]) < Math.abs(rects[nearest][edge] - viewport[edge]) ? i : nearest, 0);
    if (atStart) index = 0;
    else if (atEnd) index = count - 1;
    return { index, count, atStart, atEnd };
  }

  const core = { parseWheelSpec, safeProductUrl, safeImageUrl, normalizeCatalog, matchingProducts, filterOptions, railPosition };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('sac-kansei-finder')) return;

  class SacKanseiFinder extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.results = this.querySelector('[data-wheel-results]');
      this.status = this.querySelector('[data-wheel-status]');
      this.empty = this.querySelector('[data-wheel-empty]');
      this.more = this.querySelector('[data-wheel-more]');
      this.controls = this.querySelector('[data-wheel-controls]');
      this.railControls = this.querySelector('[data-wheel-rail-controls]');
      this.railPrev = this.querySelector('[data-wheel-prev]');
      this.railNext = this.querySelector('[data-wheel-next]');
      this.mobileQuery = window.matchMedia?.('(max-width: 749px)') || null;
      this.isMobile = Boolean(this.mobileQuery?.matches);
      this.cardTemplate = this.querySelector('template[data-wheel-card]');
      this.labels = {};
      this.selected = this.selected || new Map();
      this.limit = 6;
      try {
        const payload = JSON.parse(this.querySelector('script[data-wheel-data]')?.textContent || 'null');
        this.labels = payload?.labels || {};
        this.catalog = normalizeCatalog(payload, window.location.origin);
        if (!this.results || !this.status || !this.cardTemplate) throw new TypeError('Missing wheel finder markup');
        this.populateFilters();
        if (this.controls) this.controls.disabled = false;
        this.ready = true;
        this.render();
        this.connectRail();
      } catch {
        this.ready = false;
        if (this.controls) this.controls.disabled = true;
        if (this.status) this.status.textContent = this.label('unavailable');
        if (this.more) this.more.hidden = true;
        if (this.railControls) this.railControls.hidden = true;
      }
      this.handlers = {
        input: event => {
          if (event.target.matches('[data-wheel-query]') && this.ready) this.refresh();
        },
        change: event => {
          if (event.target.matches('[data-filter], [data-wheel-stock]') && this.ready) this.refresh();
          if (event.target.matches('[data-card-options]') && this.ready) this.selectVariant(event.target);
        },
        submit: event => {
          if (event.target.matches('[data-wheel-filters]')) {
            event.preventDefault();
            if (this.ready) this.refresh();
          }
        },
        click: event => this.handleClick(event),
        keydown: event => this.handleRailKey(event)
      };
      Object.entries(this.handlers).forEach(([type, handler]) => this.addEventListener(type, handler));
    }

    disconnectedCallback() {
      Object.entries(this.handlers || {}).forEach(([type, handler]) => this.removeEventListener(type, handler));
      this.handlers = null;
      if (this.railScroll) this.results?.removeEventListener('scroll', this.railScroll);
      if (this.railBreakpoint) {
        if (this.mobileQuery?.removeEventListener) this.mobileQuery.removeEventListener('change', this.railBreakpoint);
        else this.mobileQuery?.removeListener?.(this.railBreakpoint);
      }
      if (this.railResize) window.removeEventListener?.('resize', this.railResize);
      this.railObserver?.disconnect();
      if (this.railFrame !== undefined && this.railFrame !== null) window.cancelAnimationFrame?.(this.railFrame);
      this.railScroll = this.railBreakpoint = this.railResize = this.railObserver = this.mobileQuery = null;
      this.railFrame = null;
      this.ready = false;
    }

    label(key, substitutions = {}) {
      let value = typeof this.labels?.[key] === 'string' ? this.labels[key] : '';
      Object.entries(substitutions).forEach(([name, replacement]) => { value = value.split(`__${name}__`).join(String(replacement)); });
      return value;
    }

    setText(selector, value, within = this) {
      const target = within.querySelector(selector);
      if (target) target.textContent = value;
    }

    populateFilters() {
      this.querySelectorAll('[data-filter]').forEach(select => {
        const key = select.dataset.filter, prior = select.value;
        if (!['diameter', 'width', 'bolt', 'offset'].includes(key)) return;
        const any = select.querySelector('option[value=""]')?.cloneNode(true);
        if (!any) return;
        select.replaceChildren(any);
        filterOptions(this.catalog.products, key).forEach(value => {
          const option = document.createElement('option');
          option.value = String(value);
          option.textContent = key === 'offset' ? `${value > 0 ? '+' : ''}${value} mm` : key === 'bolt' ? String(value) : `${value}″`;
          select.append(option);
        });
        select.value = Array.from(select.options).some(option => option.value === prior) ? prior : '';
      });
    }

    filters() {
      const filters = { query: this.querySelector('[data-wheel-query]')?.value || '', stock: Boolean(this.querySelector('[data-wheel-stock]')?.checked) };
      this.querySelectorAll('[data-filter]').forEach(select => { filters[select.dataset.filter] = select.value; });
      return filters;
    }

    refresh() {
      this.limit = 6;
      this.render(true);
    }

    render(resetRail = true) {
      const filters = this.filters();
      const advancedCount = ['width', 'offset'].filter(key => filters[key] !== undefined && filters[key] !== '').length;
      this.setText('[data-advanced-count]', advancedCount ? `(${advancedCount})` : '');
      this.matches = matchingProducts(this.catalog.products, filters);
      const options = this.matches.reduce((sum, product) => sum + product.variants.length, 0);
      const cards = (this.isMobile ? this.matches : this.matches.slice(0, this.limit)).map(product => this.card(product));
      this.results.replaceChildren(...cards);
      const substitutions = { OPTIONS: options, DESIGNS: this.matches.length };
      this.status.textContent = [this.label('results', substitutions), this.catalog.skipped ? this.label('skipped', { COUNT: this.catalog.skipped }) : ''].filter(Boolean).join(' ');
      if (this.empty) this.empty.hidden = options > 0;
      if (this.more) this.more.hidden = this.isMobile || this.matches.length <= this.limit;
      if (this.isMobile && resetRail) this.scrollToRailCard(0, true);
      this.updateRail();
    }

    connectRail() {
      this.railScroll = () => this.queueRailUpdate();
      this.results.addEventListener('scroll', this.railScroll, { passive: true });
      this.railBreakpoint = event => {
        if (!this.ready || this.isMobile === event.matches) return;
        this.isMobile = event.matches;
        this.limit = 6;
        this.render(true);
      };
      if (this.mobileQuery?.addEventListener) this.mobileQuery.addEventListener('change', this.railBreakpoint);
      else this.mobileQuery?.addListener?.(this.railBreakpoint);
      if (window.ResizeObserver) {
        this.railObserver = new window.ResizeObserver(() => this.queueRailUpdate());
        this.railObserver.observe(this.results);
      } else {
        this.railResize = () => this.queueRailUpdate();
        window.addEventListener?.('resize', this.railResize, { passive: true });
      }
    }

    queueRailUpdate() {
      if (!this.ready || this.railFrame !== null && this.railFrame !== undefined) return;
      if (!window.requestAnimationFrame) return this.updateRail();
      this.railFrame = window.requestAnimationFrame(() => {
        this.railFrame = null;
        if (this.ready) this.updateRail();
      });
    }

    railGeometry() {
      const cards = Array.from(this.results?.children || []);
      const bounds = this.results?.getBoundingClientRect?.();
      if (!bounds) return null;
      const style = window.getComputedStyle?.(this.results);
      const rtl = style?.direction === 'rtl';
      const padding = parseFloat(style?.scrollPaddingInlineStart) || 0;
      const left = bounds.left + (this.results.clientLeft || 0);
      const right = left + this.results.clientWidth;
      const viewport = { left: left + (rtl ? 0 : padding), right: right - (rtl ? padding : 0) };
      const rects = cards.map(card => card.getBoundingClientRect());
      return { cards, rects, viewport, rtl, ...railPosition(rects, viewport, rtl) };
    }

    updateRail() {
      const geometry = this.isMobile ? this.railGeometry() : null;
      const active = Boolean(geometry && geometry.count > 1);
      if (this.railControls) this.railControls.hidden = !active;
      if (this.railPrev) this.railPrev.disabled = !active || geometry.atStart;
      if (this.railNext) this.railNext.disabled = !active || geometry.atEnd;
      this.setText('[data-wheel-position]', active ? this.label('rail_position', { CURRENT: geometry.index + 1, TOTAL: geometry.count }) : '');
    }

    scrollToRailCard(index, instant = false) {
      if (!this.isMobile) return;
      const geometry = this.railGeometry();
      if (!geometry?.count) return;
      const target = Math.max(0, Math.min(geometry.count - 1, index));
      const edge = geometry.rtl ? 'right' : 'left';
      const delta = geometry.rects[target][edge] - geometry.viewport[edge];
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      this.results.scrollBy?.({ left: delta, behavior: instant || reducedMotion ? 'auto' : 'smooth' });
      this.queueRailUpdate();
    }

    moveRail(step) {
      if (!this.ready || !this.isMobile) return;
      const geometry = this.railGeometry();
      if (!geometry || step < 0 && geometry.atStart || step > 0 && geometry.atEnd) return;
      const edge = geometry.rtl ? 'right' : 'left';
      const distances = geometry.rects.map(rect => (rect[edge] - geometry.viewport[edge]) * (geometry.rtl ? -1 : 1));
      const target = step > 0 ? distances.findIndex(distance => distance > 2) : distances.reduce((previous, distance, index) => distance < -2 ? index : previous, -1);
      if (target >= 0) this.scrollToRailCard(target);
    }

    handleRailKey(event) {
      if (!this.ready || !this.isMobile || event.target !== this.results || event.altKey || event.ctrlKey || event.metaKey) return;
      const geometry = this.railGeometry();
      if (!geometry?.count) return;
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.scrollToRailCard(event.key === 'Home' ? 0 : geometry.count - 1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.moveRail((event.key === 'ArrowRight' ? 1 : -1) * (geometry.rtl ? -1 : 1));
      }
    }

    card(product) {
      const fragment = this.cardTemplate.content.cloneNode(true);
      const card = fragment.firstElementChild;
      card.dataset.wheelProduct = product.id;
      this.setText('[data-card-title]', product.title, card);
      this.setText('[data-card-finish]', product.finish, card);
      const select = card.querySelector('[data-card-options]');
      select.replaceChildren();
      product.variants.forEach(variant => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.label;
        select.append(option);
      });
      const selected = product.variants.find(variant => variant.id === this.selected.get(product.id)) || product.variants.find(variant => variant.available) || product.variants[0];
      select.value = selected.id;
      this.updateCard(card, product, selected);
      return card;
    }

    updateCard(card, product, variant) {
      this.selected.set(product.id, variant.id);
      this.setText('[data-card-sku]', variant.sku, card);
      this.setText('[data-card-price]', variant.priceText, card);
      this.setText('[data-card-stock]', this.label(variant.available ? 'in_stock' : 'sold_out'), card);
      const stock = card.querySelector('[data-card-stock]');
      if (stock) stock.dataset.available = String(variant.available);
      const note = card.querySelector('[data-card-note]');
      if (note) {
        note.hidden = !variant.spec.annotation;
        note.textContent = variant.spec.annotation ? this.label('unavailable_source_note') : '';
      }
      ['[data-card-image]', '[data-card-title]', '[data-card-link]'].forEach(selector => {
        const anchor = card.querySelector(selector);
        if (anchor) anchor.setAttribute('href', variant.url);
      });
      const image = card.querySelector('[data-card-image] img');
      if (image) {
        image.hidden = !variant.image;
        if (variant.image) image.src = variant.image;
        else image.removeAttribute('src');
        image.alt = product.title;
      }
      const slot = card.querySelector('[data-card-badge]');
      const badge = this.cardTemplate.content.querySelector('sac-entry-badge')?.cloneNode(true);
      if (slot && badge) {
        badge.hidden = true;
        Object.assign(badge.dataset, { productId: product.id, variantId: variant.id, priceMin: String(variant.price), priceMax: String(variant.price) });
        slot.replaceChildren(badge);
      }
    }

    selectVariant(select) {
      const card = select.closest('[data-wheel-product]');
      const product = this.matches.find(item => item.id === card?.dataset.wheelProduct);
      const variant = product?.variants.find(item => item.id === select.value);
      if (variant) this.updateCard(card, product, variant);
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (!this.ready) return;
      if (button.matches('[data-wheel-clear]')) {
        this.querySelectorAll('[data-filter]').forEach(select => { select.value = ''; });
        const query = this.querySelector('[data-wheel-query]'), stock = this.querySelector('[data-wheel-stock]');
        if (query) query.value = '';
        if (stock) stock.checked = false;
        this.refresh();
        query?.focus({ preventScroll: true });
      }
      if (button.matches('[data-wheel-prev]')) this.moveRail(-1);
      if (button.matches('[data-wheel-next]')) this.moveRail(1);
      if (button.matches('[data-wheel-more]') && !this.isMobile) {
        const previous = this.limit;
        this.limit += 6;
        this.render(false);
        this.results.children[previous]?.querySelector('[data-card-title]')?.focus({ preventScroll: true });
      }
    }
  }
  window.customElements.define('sac-kansei-finder', SacKanseiFinder);
})();
